import React, {
  forwardRef,
  SVGProps,
  useCallback,
  useEffect,
  useState,
} from "react";
import { isMobile } from "react-device-detect";
import styled from "styled-components";
import Link from "next/link";
import { useRouter } from "next/router";
import { pathOr } from "ramda";
import MuiBox from "@material-ui/core/Box";
import MuiFade from "@material-ui/core/Fade";
import MuiTypography from "@material-ui/core/Typography";
import MuiClickAwayListener from "@material-ui/core/ClickAwayListener";
import {
  Dialog,
  useMediaQuery,
  Menu,
  MenuItem as MuiMenuItem,
  Button,
  SvgIconProps,
} from "@material-ui/core";
import IconClose from "@material-ui/icons/Close";
import Accordion, { AccordionProps } from "@material-ui/core/Accordion";
import AccordionSummary, {
  AccordionSummaryProps,
} from "@material-ui/core/AccordionSummary";
import AccordionDetails, {
  AccordionDetailsProps,
} from "@material-ui/core/AccordionDetails";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { useTheme } from "lib/contexts/themeContext";
import { useAuth } from "domain/auth/authContext";
import { useNotification } from "domain/notification/notificationContext";
import IconGoogle from "lib/assets/google.svg";
import IconFacebook from "lib/assets/facebook.svg";
import IconEmail from "lib/assets/email.svg";
import IconBell from "lib/assets/bell.svg";
import IconBellDot from "lib/assets/bell-dot.svg";
import IconBack from "lib/assets/back.svg";
import { AsyncStatus, CustomEventLabel, UIEvent } from "lib/enums";
import { useEvent } from "lib/contexts/eventContext";
import useIsResMobile from "lib/hooks/useIsResMobile";
import IconHamburger from "lib/assets/ham.svg";
import Avatar from "lib/components/Avatar";
import { fromNowRecent } from "lib/date";
import { UrlObject } from "url";
import IconChevronRight from "lib/assets/chevron-right.svg";
import { purify } from "lib/format";
import ButtonDownloadApp from "./ButtonDownloadApp";
import { useLocale, useTranslations } from "lib/i18n";
import LanguageMenu from "lib/components/LanguageMenu";

export const TOPBAR_HEIGHT = 70;

const MENU_CONFIG: MenuConfig = [
  {
    labelKey: "groups.forIndividuals",
    type: "group",
    items: [
      {
        labelKey: "items.caseTracker",
        href: "/case-tracker",
        pathRegex: /\/case-tracker/,
        type: "item",
      },
      {
        labelKey: "items.findLawyer",
        href: "/lawyers/states/all",
        pathRegex: /\/lawyers\/states/,
        type: "item",
      },
      {
        labelKey: "items.mockInterview",
        href: "/mock-interview",
        pathRegex: /\/mock-interview/,
        type: "item",
      },
    ],
  },
  {
    labelKey: "groups.forBusinesses",
    type: "group",
    items: [
      {
        labelKey: "items.lawfullyPro",
        href: "/pro",
        pathRegex: /\/pro/,
        type: "item",
        emphasized: true,
      },
      {
        labelKey: "items.lawfullyIntelligence",
        href: "/intelligence",
        pathRegex: /\/intelligence/,
        type: "item",
      },
    ],
  },
  {
    labelKey: "items.community",
    href: "/community",
    pathRegex: /\/community/,
    type: "item",
  },
  {
    labelKey: "items.immigration101",
    href: "/resources",
    pathRegex: /\/resources/,
    type: "item",
  },
  {
    labelKey: "groups.lawfullyAnalytics",
    type: "group",
    items: [
      {
        labelKey: "items.statusExplorer",
        href: "/data/uscis-status-explorer",
        pathRegex: /\/data\/uscis-status-explorer/,
        type: "item",
      },
      {
        labelKey: "items.processingTrends",
        href: "/data/trends",
        pathRegex: /\/data\/trends/,
        type: "item",
      },
      {
        labelKey: "items.h1bSimulator",
        href: "/data/h1b-lottery-simulator",
        pathRegex: /\/data\/h1b-lottery-simulator/,
        type: "item",
      },
    ],
  },
];

interface TopBarProps {
  /**
   * Custom component to render on the right side
   * of the TopBar on mobile resolutions
   */
  customCompMobile?: React.ReactNode;
}

const TopBar: React.FC<TopBarProps> = ({ customCompMobile }) => {
  const { breakpoints, zIndex } = useTheme();
  const below960 = breakpoints.down(960);
  const isResMobile = useMediaQuery(below960);

  return (
    <MuiBox
      css={`
        height: ${TOPBAR_HEIGHT}px;
      `}
      component="header"
    >
      <MuiBox
        position={isResMobile ? "relative" : "fixed"}
        zIndex={zIndex.appBar}
        top={0}
        right={0}
        left={0}
        component="nav"
        px={4}
        py={0}
        height={TOPBAR_HEIGHT}
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        bgcolor="white"
      >
        <div
          css={`
            display: none;
            ${below960} {
              display: block;
            }
          `}
        >
          <MenuMobile />
        </div>
        <div
          css={`
            width: 100%;
            ${below960} {
              display: none;
            }
          `}
        >
          <MenuDesktop />
        </div>
        {isResMobile && customCompMobile}
      </MuiBox>
    </MuiBox>
  );
};

export default TopBar;

/**
 * Mobile version of the TopBar
 */
const MenuMobile = () => {
  const t = useTranslations("nav");
  const { query, asPath } = useRouter();
  const { palette } = useTheme();
  const { state, doLogOut } = useAuth();
  const { dataProfile } = state;
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const handleOnToggleMenu = useCallback(() => {
    setIsMenuOpen((isMenuOpen) => !isMenuOpen);
  }, []);

  return (
    <>
      <IconHamburger onClick={handleOnToggleMenu} />

      <Dialog open={isMenuOpen} fullScreen>
        <MuiBox display="flex" flexDirection="column" height="100%">
          {/* top section */}
          <MuiBox
            px={4}
            py={0}
            height={TOPBAR_HEIGHT}
            minHeight={TOPBAR_HEIGHT}
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <MuiBox
              position="absolute"
              display="flex"
              left="0"
              py="21px"
              px={4}
              onClick={handleOnToggleMenu}
            >
              <IconClose />
            </MuiBox>
            <Link href="/" passHref>
              <a
                css={`
                  display: flex;
                  align-items: center;
                `}
              >
                <img
                  css={`
                    width: 140px;
                  `}
                  src="https://web-static-resources.lawfully.com/lawfully-full-logo.svg"
                  alt="Lawfully Logo"
                />
              </a>
            </Link>
            {state.isLoggedIn && (
              <MuiBox
                position="absolute"
                display="flex"
                right="0"
                py="21px"
                px={4}
              >
                <Notifications onClose={handleOnToggleMenu} />
              </MuiBox>
            )}
          </MuiBox>
          {/* menu section */}
          <MuiBox
            display="flex"
            flexDirection="column"
            justifyContent="space-between"
            flexGrow="1"
            py={4}
          >
            <MuiBox display="flex" flexDirection="column" px={0}>
              {MENU_CONFIG.map((menuItem) => {
                if (menuItem.type === "group") {
                  const regexArr = menuItem.items.map((item) => item.pathRegex);

                  // determine if this menu group is active by querying its childrens' regex paths
                  const isActive = regexArr.reduce(
                    (prev, curr) => prev || curr.test(asPath),
                    false,
                  );

                  return (
                    <StyledAccordion key={t(menuItem.labelKey)}>
                      <StyledAccordionSummary
                        expandIcon={
                          <ExpandMoreIcon style={{ color: "black" }} />
                        }
                      >
                        <MenuItemMobile
                          isActive={isActive}
                          css={`
                            padding: 0;
                            font-weight: 500;
                            color: black;
                          `}
                        >
                          {t(menuItem.labelKey)}
                        </MenuItemMobile>
                      </StyledAccordionSummary>
                      <StyledAccordionDetails>
                        {menuItem.items.map((subMenuItem) => {
                          return (
                            <Link
                              key={t(subMenuItem.labelKey)}
                              href={subMenuItem.href}
                              passHref
                            >
                              <MenuItemMobile
                                css={`
                                  background-color: #f9f9fb;
                                `}
                                isActive={subMenuItem.pathRegex.test(asPath)}
                                onClick={handleOnToggleMenu}
                              >
                                {t(subMenuItem.labelKey)}
                              </MenuItemMobile>
                            </Link>
                          );
                        })}
                      </StyledAccordionDetails>
                    </StyledAccordion>
                  );
                }

                return (
                  <Link
                    href={(menuItem as MenuItemConfig).href}
                    key={t(menuItem.labelKey)}
                  >
                    <MenuItemMobile
                      isActive={menuItem.pathRegex.test(asPath)}
                      onClick={handleOnToggleMenu}
                    >
                      {t(menuItem.labelKey)}
                    </MenuItemMobile>
                  </Link>
                );
              })}
              <ButtonDownloadApp
                css={`
                  margin: 20px 0 40px 20px;
                `}
                event={UIEvent.NavMobileAppDownload}
              />
            </MuiBox>
            {state.isLoggedIn ? (
              <>
                {/* logged in menu */}
                <MuiBox
                  display="flex"
                  flexDirection="column"
                  alignItems="left"
                  px={5}
                  pt="25px"
                  bgcolor="#F9F9FB"
                  borderTop={`1px solid ${palette.grey[100]}`}
                >
                  <AccountContainer
                    css={`
                      margin-bottom: 15px;
                    `}
                  >
                    {dataProfile ? (
                      <MuiBox display="flex" alignItems="center">
                        <Avatar
                          css={`
                            margin-right: 10px;
                            width: 30px;
                            height: 30px;
                          `}
                          src={dataProfile?.profileImage}
                          letter={dataProfile?.firstName[0]}
                        />
                        <MuiBox
                          fontSize="16px"
                          lineHeight="24px"
                          fontWeight="500"
                        >
                          {dataProfile?.firstName} {dataProfile?.lastName}
                        </MuiBox>
                      </MuiBox>
                    ) : (
                      <button
                        className="Account-button"
                        css={`
                          height: 60px;
                        `}
                      >
                        <div className="Account-button-icon">
                          <ProviderIcon
                            provider={pathOr(
                              "email",
                              ["provider"],
                              state.dataUser,
                            )}
                          />
                        </div>
                        <MuiTypography variant="body1">
                          {state.dataUser?.email}
                        </MuiTypography>
                      </button>
                    )}
                  </AccountContainer>
                  <Link href={{ pathname: "/consultations" }}>
                    <MenuItemMobileLight>
                      {t("account.appointments")}
                    </MenuItemMobileLight>
                  </Link>
                  <Link href={{ pathname: "/community/activity" }}>
                    <MenuItemMobileLight>
                      {t("account.communityActivities")}
                    </MenuItemMobileLight>
                  </Link>
                  <Link href={{ pathname: "/profile" }}>
                    <MenuItemMobileLight>
                      {t("account.profileSettings")}
                    </MenuItemMobileLight>
                  </Link>
                  <Link href={{ pathname: "/change-password" }}>
                    <MenuItemMobileLight>
                      {t("account.changePassword")}
                    </MenuItemMobileLight>
                  </Link>
                  <MenuItemMobileLight onClick={doLogOut}>
                    {t("account.logout")}
                  </MenuItemMobileLight>
                </MuiBox>
              </>
            ) : (
              <>
                {/* logged out menu */}
                <MuiBox
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                >
                  <Link href={{ pathname: "/login", query }}>
                    <MenuItemMobileLight
                      css={`
                        padding: 0 30px;
                        color: black;
                      `}
                    >
                      {t("account.login")}
                    </MenuItemMobileLight>
                  </Link>
                </MuiBox>
              </>
            )}
          </MuiBox>
        </MuiBox>
      </Dialog>
    </>
  );
};

type MenuConfig = Array<MenuItemConfig | MenuGroupConfig>;

/**
 * Represents a group of navigational menu items
 */
interface MenuGroupConfig {
  labelKey: string;
  items: Array<MenuItemConfig>;
  type: "group";
}

/**
 * Represents a single navigational menu item
 */
interface MenuItemConfig {
  href: string | UrlObject;
  labelKey: string;
  pathRegex: RegExp;
  type: "item";
  emphasized?: boolean;
}

const MenuGroupContainer = styled.div`
  position: absolute;
  top: 50px;
  min-width: 280px;
  background-color: white;
  border-radius: ${({ theme }) => theme.shape.borderRadius}px;
  box-shadow: 0px 0px 10px rgba(134, 140, 176, 0.2);
  padding: 20px;
`;

/**
 * Desktop version of the TopBar
 */
const MenuDesktop = () => {
  const t = useTranslations("nav");
  const { state } = useAuth();
  const {
    dataProfile,
    status: { dataProfile: statusDataProfile },
  } = state;
  const { pathname, query, asPath } = useRouter();

  const [isMenuOpen, setIsMenuOpen] = useState<{
    [menuItemKey: string]: boolean;
  }>({});

  const makeHandleOnOpenMenu = useCallback(
    (key: string) => () => {
      setIsMenuOpen({
        [key]: true,
      });
    },
    [],
  );

  const handleOnCloseMenu = useCallback(
    (key: string) => () => {
      setIsMenuOpen((config) => ({ ...config, [key]: false }));
    },
    [],
  );

  return (
    <MuiBox display="flex" flex={1} alignItems="center">
      <Link href="/" passHref>
        <a
          css={`
            display: flex;
            align-items: center;
          `}
        >
          <img
            css={`
              width: 140px;
            `}
            src="https://web-static-resources.lawfully.com/lawfully-full-logo.svg"
            alt="Lawfully Logo"
          />
        </a>
      </Link>

      <MuiBox display="flex" flex={1}>
        <div
          css={`
            flex: 1;
            margin-left: 60px;
            display: flex;
            align-items: center;
          `}
        >
          {MENU_CONFIG.map((menuItem) => {
            if (menuItem.type === "group") {
              const regexArr = menuItem.items.map((item) => item.pathRegex);

              // determine if this menu group is active by querying its childrens' regex paths
              const isActive = regexArr.reduce(
                (prev, curr) => prev || curr.test(asPath),
                false,
              );

              // menu group
              return (
                <MuiClickAwayListener
                  mouseEvent={isMenuOpen[menuItem.labelKey] ? "onClick" : false}
                  onClickAway={handleOnCloseMenu(menuItem.labelKey)}
                  key={t(menuItem.labelKey)}
                >
                  <div
                    css={`
                      position: relative;
                    `}
                  >
                    {/* Button */}
                    <MenuItem
                      href={""}
                      key={t(menuItem.labelKey)}
                      isActive={isActive}
                    >
                      <MuiBox
                        display="flex"
                        alignItems="center"
                        onClick={makeHandleOnOpenMenu(menuItem.labelKey)}
                      >
                        <div
                          css={`
                            font-weight: 500;
                          `}
                        >
                          {t(menuItem.labelKey)}
                        </div>
                        <IconChevronRight
                          css={`
                            margin: 0 4px;
                            transform: rotate(90deg);
                          `}
                          strokeWidth="2"
                          stroke="black"
                        />
                      </MuiBox>
                    </MenuItem>

                    {/* Menu */}
                    <MuiFade in={isMenuOpen[menuItem.labelKey]}>
                      <MuiBox>
                        <MenuGroupContainer>
                          {menuItem.items.map((childMenuItem) => (
                            <MenuItem
                              href={childMenuItem.href}
                              key={t(childMenuItem.labelKey)}
                              isActive={childMenuItem.pathRegex.test(asPath)}
                            >
                              <MuiBox display="flex" alignItems="center">
                                {t(childMenuItem.labelKey)}
                              </MuiBox>
                            </MenuItem>
                          ))}
                        </MenuGroupContainer>
                      </MuiBox>
                    </MuiFade>
                  </div>
                </MuiClickAwayListener>
              );
            }
            return (
              <MenuItem
                href={menuItem.href}
                key={t(menuItem.labelKey)}
                isActive={menuItem.pathRegex.test(asPath)}
              >
                <MuiBox display="flex" alignItems="center">
                  <div
                    css={`
                      font-weight: ${menuItem.emphasized ? "500" : "400"};
                    `}
                  >
                    {t(menuItem.labelKey)}
                  </div>
                </MuiBox>
              </MenuItem>
            );
          })}
        </div>

        {/* Outside the token check on purpose: choosing a language has nothing to
            do with being signed in, and that check gates the whole subtree. */}
        <MuiBox display="flex" alignItems="center" style={{ gap: 12 }}>
          <LanguageMenu />
          {state.hasValidatedSavedToken && (
            <MuiBox display="flex" alignItems="center">
              {state.isLoggedIn &&
                (statusDataProfile === AsyncStatus.Success ||
                  statusDataProfile === AsyncStatus.Error) && (
                  <>
                    <MuiBox
                      mr={4}
                      width="40px"
                      height="40px"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      css={`
                        &:hover {
                          background-color: #f3f4f8;
                          transition: background-color 0.2s linear;
                          border-radius: 50%;
                          cursor: pointer;
                        }
                      `}
                    >
                      <Account user={state.dataUser} profile={dataProfile} />
                    </MuiBox>
                    <MuiBox
                      mr={3}
                      width="40px"
                      height="40px"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      css={`
                        &:hover {
                          background-color: #f3f4f8;
                          transition: background-color 0.2s linear;
                          border-radius: 50%;
                          cursor: pointer;
                        }
                      `}
                    >
                      <Notifications />
                    </MuiBox>
                  </>
                )}
              {/* Only render LOGIN button on views other than the LOGIN VIEW itself */}
              {!state.isLoggedIn && !pathname.includes("login") && (
                <MuiBox letterSpacing="1px">
                  <LoginMenu />
                </MuiBox>
              )}
            </MuiBox>
          )}
        </MuiBox>
      </MuiBox>
    </MuiBox>
  );
};

/**
 * Login dropdown menu component
 */
const LoginMenu: React.FC = () => {
  const t = useTranslations("nav");
  const { query, asPath } = useRouter();
  const { push } = useRouter();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleOnClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setAnchorEl(event.currentTarget);
    },
    [],
  );

  const handleOnClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleOnMobileCaseTracker = useCallback(() => {
    push({
      pathname: "/login",
      query: { redirect: query.redirect || asPath },
    });
    handleOnClose();
  }, [push, query, asPath, handleOnClose]);

  const handleOnLawfullyPro = useCallback(() => {
    window.open("https://pro.lawfully.com/sign-in", "_blank");
    handleOnClose();
  }, [handleOnClose]);

  return (
    <>
      <LoginMenuButton
        onClick={handleOnClick}
        css={`
          margin-right: 0;
        `}
      >
        <Button>
          <MuiTypography color="textPrimary" variant="body1">
            {t("account.logIn")}
          </MuiTypography>
        </Button>
      </LoginMenuButton>

      <Menu
        id="login-menu"
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleOnClose}
        getContentAnchorEl={null}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        PaperProps={{
          style: {
            width: "220px",
            marginTop: "8px",
            borderRadius: "8px",
            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
          },
        }}
        MenuListProps={{
          style: {
            padding: "0",
          },
        }}
      >
        <MuiBox px={0.5} py={0.5} borderBottom="1px solid #f0f0f0">
          <MuiBox px={2} py={1.5}>
            <MuiTypography
              variant="caption"
              style={{
                fontSize: "14px",
                fontWeight: 500,
                lineHeight: "24px",
                color: "#000",
              }}
            >
              {t("account.logInSelect")}
            </MuiTypography>
          </MuiBox>
        </MuiBox>

        <MuiBox padding={0.5}>
          <Button
            css={`
              justify-content: start !important;
              border-radius: 6px !important;
              width: 100%;
            `}
            onClick={handleOnMobileCaseTracker}
          >
            <MuiTypography
              variant="body1"
              style={{
                fontSize: "14px",
                lineHeight: "20px",
                fontWeight: 500,
                color: "#334155",
                marginRight: "12px",
              }}
            >
              {t("account.mobileCaseTracker")}
            </MuiTypography>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
            >
              <path
                d="M3.33337 8.00049H12.6667"
                stroke="#334155"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M8 3.33398L12.6667 8.00065L8 12.6673"
                stroke="#334155"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </Button>
        </MuiBox>

        <MuiBox padding={0.5}>
          <Button
            css={`
              justify-content: start !important;
              border-radius: 6px !important;
              width: 100%;
            `}
            onClick={handleOnLawfullyPro}
          >
            <MuiTypography
              variant="body1"
              style={{
                fontSize: "14px",
                lineHeight: "20px",
                fontWeight: 500,
                color: "#334155",
                marginRight: "12px",
              }}
            >
              {t("account.proForBusiness")}
            </MuiTypography>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
            >
              <path
                d="M3.33337 8.00049H12.6667"
                stroke="#334155"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M8 3.33398L12.6667 8.00065L8 12.6673"
                stroke="#334155"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </Button>
        </MuiBox>
      </Menu>
    </>
  );
};

const LoginMenuButton = styled.button`
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  cursor: pointer;
  letter-spacing: 1px;
  padding: 0;
  font-size: inherit;
  font-family: inherit;
`;

interface AccountProps {
  user: ResUser | null;
  profile: ResProfile | null;
}

/**
 * Displays account and settings menu.
 */
const Account: React.FC<AccountProps> = ({ user, profile }) => {
  const t = useTranslations("nav");
  const { query } = useRouter();
  // State. Is menu visible?
  const [open, setOpen] = useState(false);
  /**
   * Handles email click.
   */
  const handleOnEmailClick = useCallback(() => {
    setOpen((val) => !val);
  }, [setOpen]);
  /**
   * Handles close.
   */
  const handleOnClose = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  if (!user) {
    return (
      <MuiBox letterSpacing="1px">
        <Link href={{ pathname: "/login", query }}>
          <MuiTypography color="textPrimary" variant="body1">
            {t("account.login")}
          </MuiTypography>
        </Link>
      </MuiBox>
    );
  }

  return (
    <MuiClickAwayListener
      onClickAway={handleOnClose}
      mouseEvent={open ? "onClick" : false}
    >
      <AccountContainer>
        {/* Button */}
        <button className="Account-button" onClick={handleOnEmailClick}>
          {/* Display profile, if possible. */}
          {profile ? (
            <MuiBox display="flex" alignItems="center">
              <Avatar
                css={`
                  width: 30px;
                  height: 30px;
                `}
                src={profile.profileImage}
                letter={profile.firstName[0]}
              />
            </MuiBox>
          ) : (
            <>
              <div className="Account-button-icon">
                <ProviderIcon provider={pathOr("email", ["provider"], user)} />
              </div>
            </>
          )}
        </button>

        {/* Menu */}
        <MuiFade in={open}>
          <MuiBox>
            <AccountMenu onClose={handleOnClose} profile={profile} />
          </MuiBox>
        </MuiFade>
      </AccountContainer>
    </MuiClickAwayListener>
  );
};

const AccountContainer = styled.div`
  position: relative;
  z-index: 10;
  .Account-button {
    border: none;
    display: flex;
    align-items: center;
    justify-content: center;
    background: none;
    cursor: pointer;
  }
  .Account-button-icon {
    display: flex;
  }
`;

const NotificationsContainer = styled.div`
  position: relative;
  z-index: 11;
  .Notifications-button {
    border: none;
    display: flex;
    align-items: center;
    justify-content: center;
    background: none;
    cursor: pointer;
  }
  .Notifications-button-icon {
    display: flex;
  }
`;

interface AccountMenuProps {
  onClose: () => void;
  profile: ResProfile | null;
}

const AccountMenu: React.FC<AccountMenuProps> = ({ onClose, profile }) => {
  const t = useTranslations("nav");
  const {
    state: { dataUser },
    doLogOut,
  } = useAuth();
  const { tag } = useEvent();
  const { push } = useRouter();

  const handleOnChangePassword = useCallback(() => {
    push("/change-password");
    onClose();
  }, [push, onClose]);

  const handleOnLogOut = useCallback(() => {
    tag?.(CustomEventLabel.TopNavLogOut);
    doLogOut();
  }, [tag, doLogOut]);

  const handleOnMyAppointment = useCallback(() => {
    push("/consultations");
    onClose();
  }, [push, onClose]);

  const handleOnCommunityActivity = useCallback(() => {
    push("/community/activity");
    onClose();
  }, [push, onClose]);

  const handleOnProfileSettings = useCallback(() => {
    push("/profile");
    onClose();
  }, [push, onClose]);

  return (
    <AccountMenuContainer>
      {(profile || dataUser?.email) && (
        <div
          css={`
            font-weight: bold;
            font-size: 16px;
            line-height: 24px;
            padding: 10px 20px;
            overflow: hidden;
            white-space: nowrap;
            text-overflow: ellipsis;
          `}
        >
          {profile
            ? `${profile.firstName} ${profile.lastName}`
            : dataUser?.email}
        </div>
      )}
      <button className="AccountMenu-item" onClick={handleOnMyAppointment}>
        {t("account.appointments")}
      </button>
      <button className="AccountMenu-item" onClick={handleOnCommunityActivity}>
        {t("account.communityActivities")}
      </button>
      <button className="AccountMenu-item" onClick={handleOnProfileSettings}>
        {t("account.profileSettings")}
      </button>
      {dataUser && !("provider" in dataUser && dataUser.provider) && (
        <button className="AccountMenu-item" onClick={handleOnChangePassword}>
          {t("account.changePassword")}
        </button>
      )}
      <button className="AccountMenu-item" onClick={handleOnLogOut}>
        {t("account.logOut")}
      </button>
    </AccountMenuContainer>
  );
};

const AccountMenuContainer = styled.div`
  position: absolute;
  top: 50px;
  right: -20px;
  border-radius: ${({ theme }) => theme.shape.borderRadius}px;
  box-shadow: 0px 0px 10px rgba(134, 140, 176, 0.2);
  padding: 10px 0;
  width: 260px;
  background-color: ${({ theme }) => theme.palette.common.white};
  .AccountMenu-item {
    outline: none;
    border: none;
    padding: 18px 20px;
    width: 100%;
    display: flex;
    align-items: center;
    line-height: 24px;
    font-size: 16px;
    background-color: ${({ theme }) => theme.palette.common.white};
    cursor: pointer;
    ${({ theme }) =>
      `transition: background-color ${theme.transitions.duration.shortest}ms ${theme.transitions.easing.easeInOut};`}
    :focus, :hover {
      background-color: #f3f4f8;
    }
  }
`;

interface ProviderIconProps {
  provider: "google" | "facebook" | "email";
}

const ProviderIcon: React.FC<ProviderIconProps> = ({ provider }) => {
  const theme = useTheme();

  return provider === "google" ? (
    <IconGoogle />
  ) : provider === "facebook" ? (
    <IconFacebook />
  ) : (
    <IconEmail fill={theme.palette.common.black} />
  );
};

const MenuItemMobile = styled.button<{ isActive?: boolean }>`
  text-align: left;
  letter-spacing: 1px;
  font-size: 16px;
  line-height: 20px;
  height: 60px;
  background: transparent;
  outline: none;
  border: none;
  padding: 0 30px;
  width: 100%;
  font-weight: ${({ isActive }) => (isActive ? "600" : "400")};
  color: #555;
`;

const MenuItemMobileLight = styled(MenuItemMobile)`
  padding: 0;
  font-weight: normal;
`;

/**
 * Displays a button and a list of notifications.
 */
const Notifications = (props: { onClose?: () => void }) => {
  const {
    state: { dataListNotification },
    doGetListNotification,
    doReadListNotification,
  } = useNotification();
  // Is menu open?
  const [isOpen, setIsOpen] = useState(false);
  /**
   * Handles menu open.
   */
  const handleOnOpen = useCallback(() => {
    setIsOpen((val) => !val);
    // Mark all notifications as read.
    doReadListNotification();
  }, [doReadListNotification]);
  /**
   * Handles menu close.
   */
  const handleOnClose = useCallback(() => {
    setIsOpen(false);
    props.onClose?.();
  }, [props]);

  // Retrieve notifications.
  useEffect(() => {
    doGetListNotification();
  }, [isOpen, doGetListNotification]);

  return (
    <MuiClickAwayListener
      onClickAway={handleOnClose}
      mouseEvent={isOpen ? "onClick" : false}
      touchEvent={isOpen ? "onTouchEnd" : false}
    >
      <NotificationsContainer>
        {/* Button */}
        <button className="Notifications-button" onClick={handleOnOpen}>
          <div className="Notifications-button-icon">
            {dataListNotification && dataListNotification.unreadCount > 0 ? (
              <IconBellDot />
            ) : (
              <IconBell />
            )}
          </div>
        </button>
        {/* Notification items */}
        <MuiFade in={isOpen}>
          <MuiBox>
            <NotificationContents onClose={handleOnClose} />
          </MuiBox>
        </MuiFade>
      </NotificationsContainer>
    </MuiClickAwayListener>
  );
};

interface NotificationContentsProps {
  onClose: () => void;
}

const NotificationContents: React.FC<NotificationContentsProps> = ({
  onClose,
}) => {
  const t = useTranslations("nav");
  const isResMobile = useIsResMobile();
  const router = useRouter();
  const {
    state: { dataListNotification },
  } = useNotification();
  /**
   * Handles notification item click.
   */
  const handleOnItemClick = useCallback(
    async (navigation: ResNotification["navigation"]) => {
      const match = navigation.match(/community\/posts\/(\d+)/);
      if (!match) return;
      const postId = match[1];
      await router.push(
        { pathname: `/community/posts/[postId]` },
        { pathname: `/community/posts/${postId}` },
      );
      onClose();
    },
    [onClose, router],
  );

  return (
    <NotificationContentsContainer>
      {isResMobile ? (
        <MuiBox mb={2} position="relative">
          <MuiBox
            display="flex"
            alignItems="center"
            justifyContent="center"
            height="60px"
            lineHeight="22px"
            fontSize="16px"
            fontWeight={700}
            textAlign="center"
          >
            {t("account.notifications")}
          </MuiBox>
          <MuiBox position="absolute" left={0} top={0}>
            <IconBack onClick={onClose} />
          </MuiBox>
        </MuiBox>
      ) : (
        <MuiBox
          position="relative"
          px="20px"
          py="10px"
          lineHeight="24px"
          fontSize="16px"
          fontWeight={700}
        >
          {t("account.notifications")}
        </MuiBox>
      )}
      <MuiBox flex={1} overflow="auto">
        {dataListNotification?.items.map((item) => (
          <NotificationItem
            key={item.notificationId}
            contents={item.contents}
            imageUrl={item.imageUrl}
            date={item.createdAt}
            navigation={item.navigation}
            onClick={handleOnItemClick}
          />
        ))}
      </MuiBox>
    </NotificationContentsContainer>
  );
};

const NotificationContentsContainer = styled.div`
  position: absolute;
  top: 50px;
  right: -15px;
  border-radius: ${({ theme }) => theme.shape.borderRadius}px;
  box-shadow: 0px 0px 10px rgba(134, 140, 176, 0.2);
  padding: 10px 0;
  width: 360px;
  max-height: 575px;
  display: flex;
  flex-direction: column;
  background-color: ${({ theme }) => theme.palette.common.white};
  ${(props) => `${props.theme.breakpoints.down("sm")} {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    width: 100%;
    max-height: 100%;
  `}
`;

const NotificationItem = ({
  contents,
  imageUrl,
  date,
  navigation,
  onClick,
}) => {
  const locale = useLocale();
  const { transitions } = useTheme();

  const handleOnClick = useCallback(() => {
    onClick(navigation);
  }, [navigation, onClick]);

  return (
    <button
      css={`
        padding: 20px 20px 10px;
        display: flex;
        border: none;
        width: 100%;
        background: none;
        text-align: left;
        cursor: pointer;
        transition: background-color ${transitions.duration.shortest}ms
          ${transitions.easing.easeInOut};
        :hover {
          background-color: #f3f4f8;
        }
      `}
      onClick={handleOnClick}
    >
      <Avatar
        css={`
          margin-right: 10px;
        `}
        src={imageUrl}
      />
      <MuiBox flex={1} overflow="hidden">
        <MuiBox lineHeight="22px" fontSize="14px;">
          <span dangerouslySetInnerHTML={{ __html: purify(contents) }} />
        </MuiBox>
        <MuiBox lineHeight="16px" fontSize="12px" color="#777">
          {fromNowRecent(date, locale)}
        </MuiBox>
      </MuiBox>
    </button>
  );
};

interface MenuItemProps {
  href: string | UrlObject;
  className?: string;
  isActive?: boolean;
}

const MenuItem: React.FC<MenuItemProps> = ({
  href,
  children,
  className,
  isActive,
}) => {
  return (
    <>
      {href ? (
        <Link href={href} passHref>
          <MenuAnchor isActive={isActive} className={className}>
            {children}
          </MenuAnchor>
        </Link>
      ) : (
        <MenuAnchor isActive={isActive} className={className}>
          {children}
        </MenuAnchor>
      )}
    </>
  );
};

type MenuAnchorProps = {
  className?: string;
  isActive?: boolean;
  href?: string | UrlObject;
  children: React.ReactNode;
  onClick?: () => void;
};

/**
 * In order for next/Link to work properly, we must wrap the function component with
 * forwardRef
 * https://nextjs.org/docs/api-reference/next/link#if-the-child-is-a-function-component
 */
const MenuAnchor = forwardRef<HTMLAnchorElement, MenuAnchorProps>(
  ({ className, isActive, children, href, onClick }, ref) => (
    <a
      ref={ref}
      href={href as string}
      onClick={onClick}
      className={className}
      css={`
        display: inline-block;
        position: relative;
        margin: 10px 20px 10px 0;
        line-height: 20px;
        font-size: 16px;
        letter-spacing: 0.2px;
        color: black;
        ${isActive ? `font-weight: 600;` : `font-weight: 400;`}
        :hover {
          color: black;
        }
      `}
    >
      <div
        css={`
          padding: 8px 10px;
          border-radius: 10px;
          &:hover {
            transition: background-color 0.2s linear;
            background-color: #f3f4f8;
          }
        `}
      >
        {children}
      </div>
    </a>
  ),
);

const StyledAccordion = styled((props: AccordionProps) => (
  <Accordion
    {...props}
    classes={{ expanded: "expanded" }}
    TransitionProps={{ unmountOnExit: true }}
  />
))`
  && {
    border: none;
    border-radius: none;
    width: 100%;
    box-shadow: none;
    &.expanded {
      margin: 0 auto;
    }
    &:before {
      display: none;
    }
  }
`;

const StyledAccordionSummary = styled((props: AccordionSummaryProps) => (
  <AccordionSummary
    {...props}
    classes={{ content: "content", expandIcon: "expandIcon" }}
  />
))`
  &&& {
    margin-bottom: -1;
    padding: 0 30px;
    min-height: auto;
    line-height: 24px;
    font-weight: 500;
    font-size: 16px;
    .content {
      margin: 0;
    }
  }
`;

const StyledAccordionDetails = styled((props: AccordionDetailsProps) => (
  <AccordionDetails {...props} />
))`
  && {
    padding: 10px 0;
    display: block;
    line-height: 22px;
    font-size: 14px;
    white-space: pre-wrap;
  }
`;
